import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Send,
  X,
  Plus,
  Trash2,
  Clock,
  Maximize2,
  Minimize2,
  RotateCcw,
  Mic,
  Upload,
  FileText,
} from "lucide-react";
import { ChatMessage } from "../types";
import { usePermissions } from "../contexts/PermissionsContext";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import { useTheme } from "../contexts/ThemeContext";
import { useOpenAIRealtime } from "../hooks/useOpenAIRealtime";
import { useFeatureFlags } from "../contexts/FeatureFlagContext";
import { ComingSoon } from "./common/ComingSoon";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { RecommendOptions } from "./common/RecommendOptions";
import { VoiceVisualizer } from "./common/VoiceVisualizer";
import { ProjectPreviewMessage } from "./common/ProjectPreviewMessage";
import { ProjectSummaryCard } from "./common/ProjectSummaryCard";
import { RecommendationCard } from "./common/RecommendationCard";
import { SearchResultCard } from "./common/SearchResultCard";
import { EntityConfirmationCard } from "./common/EntityConfirmationCard";
import { PlanApprovalCard } from "./common/PlanApprovalCard";
import { DemoPrompts } from "./dev/DemoPrompts";
import { useWalkthrough } from "../contexts/WalkthroughContext";
import { getWalkthrough } from "../walkthroughs/walkthroughConfigs";
import { formatDateTime } from "../utils/dateUtils";
import { useTokenCheck } from "../hooks/useTokenCheck";
import { useToken } from "../contexts/TokenContext";

interface AIPaneProps {
  onClose: () => void;
}

interface Conversation {
  id: string;
  title: string;
  userId: string;
  userName: string;
  userEmail: string;
  isOwn: boolean;
  lastMessageAt: string;
  createdAt: string;
}

export const AIPane: React.FC<AIPaneProps> = ({ onClose }) => {
  const { language, t, isRTL } = useTranslation();
  const { theme } = useTheme();
  const { startWalkthrough } = useWalkthrough();
  const { isFeatureEnabled } = useFeatureFlags();
  const { handleTokenError } = useTokenCheck();
  const { openPurchaseModal } = useToken();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Detect if dark mode is active and update when theme changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark =
        theme.mode === "dark" ||
        (theme.mode === "system" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches) ||
        document.documentElement.classList.contains("dark");
      setIsDarkMode(isDark);
    };

    checkDarkMode();

    // Listen for system theme changes
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    mediaQuery.addEventListener("change", checkDarkMode);

    // Listen for class changes on document
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      mediaQuery.removeEventListener("change", checkDarkMode);
      observer.disconnect();
    };
  }, [theme]);

  // Initialize greeting message only once on mount if no conversation will be loaded
  // The greeting will be set by startNewConversation or when no conversations exist
  // We don't set it here to avoid overriding conversations that will be loaded
  const [inputValue, setInputValue] = useState("");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<
    string | null
  >(null);
  const [showConversations, setShowConversations] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState<
    string | null
  >(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasAttemptedAutoLoad = useRef<boolean>(false);

  // AI Architect Panel (Dev Mode)
  const isDevelopment = process.env.NODE_ENV === "development";

  // Voice conversation state
  // Process voice transcript through MCP
  const processVoiceTranscript = async (userText: string) => {
    console.log("[AIPane] Processing voice transcript through MCP:", userText);

    // Add user message to chat immediately
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: userText,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      // Send to MCP endpoint
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userText,
          conversationId: currentConversationId || undefined,
          language: language || "en",
        }),
      });

      const data = await response.json();
      console.log("[AIPane] MCP response for voice:", data);

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Insufficient tokens. Please purchase more tokens to continue using AI features.",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
        setLoading(false);
        return;
      }

      if (data.success) {
        const aiResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.data.message,
          role: "assistant",
          timestamp: new Date(),
          // MCP enhancements - includes actions, clarifications, etc.
          actionId: data.data.actionId,
          actionType: data.data.actionType,
          actionStatus: data.data.requiresConfirmation ? "pending" : undefined,
          requiresConfirmation: data.data.requiresConfirmation,
          parameters: data.data.parameters,
          type: data.data.type,
          clarificationType: data.data.clarificationType,
          clarificationField: data.data.clarificationField, // Store the actual field name
          clarificationOptions: data.data.options,
          detectedEntities: data.data.detectedEntities,
          availableActions: data.data.availableActions,
          // Plan approval (multi-step workflows)
          plan: data.data.plan,
          actions: data.data.actions,
          requiresUserAction: data.data.requiresUserAction,
        };
        setMessages((prev) => [...prev, aiResponse]);

        // Update conversation ID if new
        if (data.data.conversationId && !currentConversationId) {
          setCurrentConversationId(data.data.conversationId);
          loadConversations();
        }
      } else {
        // Show the actual error message from backend
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Failed to get MCP response",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error("[AIPane] Voice MCP error:", error);
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error ? error.message : t("ai.error"),
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setLoading(false);
    }
  };

  const {
    isConnected: voiceConnected,
    isRecording: voiceRecording,
    isSpeaking: voiceSpeaking,
    connect: voiceConnect,
    disconnect: voiceDisconnect,
  } = useOpenAIRealtime({
    conversationId: currentConversationId || undefined,
    onTranscriptUpdate: (userText, assistantText) => {
      // When user speaks, process through MCP
      if (userText) {
        processVoiceTranscript(userText);
      }
      // Ignore OpenAI's assistant response since we're using MCP instead
    },
    onError: (error) => {
      console.error("[Voice] Error:", error);
    },
    onConnect: () => {
      console.log("[Voice] Connected");
    },
    onDisconnect: () => {
      console.log("[Voice] Disconnected");
    },
  });

  const handleVoiceToggle = async () => {
    console.log(
      "[AIPane] Voice toggle clicked, current state:",
      voiceConnected,
    );
    if (voiceConnected) {
      console.log("[AIPane] Disconnecting voice...");
      voiceDisconnect();
    } else {
      console.log("[AIPane] Connecting voice...");
      try {
        await voiceConnect();
        console.log("[AIPane] Voice connect initiated");
      } catch (error) {
        console.error("[AIPane] Voice connect failed:", error);
        alert(
          `Voice connection failed: ${error instanceof Error ? error.message : "Unknown error"}`,
        );
      }
    }
  };

  // Clarification Selection Handler
  const handleClarificationSelect = async (option: any, messageId: string) => {
    // Check if this is a "create new" action
    if (option.id === "__create_new__" && option.metadata?.action) {
      console.log("[AI] Create new action triggered:", option.metadata.action);
      handleManualAction(option.metadata.action, {});
      return; // Don't send to backend, just open the modal
    }

    // Find the message that needs clarification
    const clarificationMessage = messages.find((m) => m.id === messageId);
    if (!clarificationMessage) {
      console.error("[AI] Clarification message not found:", messageId);
      return;
    }

    console.log("[AI] Clarification message found:", {
      id: messageId,
      actionType: clarificationMessage.actionType,
      parameters: clarificationMessage.parameters,
      clarificationType: clarificationMessage.clarificationType,
    });

    // Mark the clarification message as answered
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === messageId ? { ...msg, clarificationAnswered: true } : msg
      )
    );

    // Compact selection display - just show the selected item
    const userMessage = `✓ ${option.label}`;
    const previousMessages = messages.map((msg) =>
      msg.id === messageId ? { ...msg, clarificationAnswered: true } : msg
    );

    // Add user message immediately (compact format)
    const newMessages = [
      ...previousMessages,
      {
        id: Date.now().toString(),
        role: "user" as const,
        content: userMessage,
        timestamp: new Date(),
      },
    ];
    setMessages(newMessages);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      // Get the last user message's intent from conversation history
      // The clarification was triggered by a user message, we need to find the original intent
      const lastUserMessage = messages
        .slice()
        .reverse()
        .find((m) => m.role === "user" && m.id !== messageId);
      // GenUI: Backend should provide originalIntent in clarification response
      // Fallback to extracting from message history, but don't hardcode default
      const originalIntent =
        clarificationMessage.actionType ||
        lastUserMessage?.actionType ||
        undefined; // Don't assume default - let backend handle it

      // Use clarificationField directly if available, otherwise fallback to extracting from clarificationType
      const clarificationField =
        clarificationMessage.clarificationField ||
        clarificationMessage.clarificationType?.replace("select_", "");

      console.log("[AI] Sending clarification response:", {
        selectedOption: option.label,
        originalIntent,
        originalParameters: clarificationMessage.parameters,
        clarificationField: clarificationField,
      });

      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: userMessage,
          conversationId: currentConversationId,
          language: language || "en",
          clarificationResponse: {
            selectedOption: option,
            originalIntent: originalIntent,
            originalParameters: clarificationMessage.parameters || {},
            clarificationField: clarificationField, // Use the actual field name
          },
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Trigger AI Architect Panel in dev mode
        // Note: AI Architect Panel is now accessible from the header

        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message,
          timestamp: new Date(),
          actionId: data.data.actionId,
          actionType: data.data.actionType,
          actionStatus: data.data.requiresConfirmation
            ? "pending"
            : "completed",
          requiresConfirmation: data.data.requiresConfirmation,
          parameters: data.data.parameters,
          type: data.data.type,
          clarificationType: data.data.clarificationType,
          clarificationField: data.data.clarificationField,
          clarificationFieldType: data.data.clarificationFieldType,
          clarificationPlaceholder: data.data.clarificationPlaceholder,
          multiSelect: data.data.multiSelect,
          clarificationOptions: data.data.options,
          fieldMetadata: data.data.fieldMetadata,
          // ✅ Include entity linking and manual actions
          detectedEntities: data.data.detectedEntities,
          availableActions: data.data.availableActions,
          // Plan approval (multi-step workflows)
          plan: data.data.plan,
          actions: data.data.actions,
          requiresUserAction: data.data.requiresUserAction,
        };

        console.log(
          "[AIPane] Clarification response - assistantMessage with availableActions:",
          assistantMessage.availableActions,
        );

        setMessages([...newMessages, assistantMessage]);

        if (!currentConversationId && data.data.conversationId) {
          setCurrentConversationId(data.data.conversationId);
        }
      } else {
        // Show the actual error message from backend
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "Failed to get response",
          timestamp: new Date(),
        };
        setMessages([...newMessages, errorMessage]);
      }
    } catch (error) {
      console.error("Error sending clarification:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: error instanceof Error ? error.message : t("ai.error"),
        timestamp: new Date(),
      };
      setMessages([...newMessages, errorMessage]);
    } finally {
      setLoading(false);
      // Scroll to messages after sending
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // Entity Confirmation Handler
  // GenUI: Use generic action contract instead of confirmedEntity/forceIntent flags
  const handleEntityConfirmation = async (message: ChatMessage) => {
    console.log(
      "[AIPane] Confirming entity creation:",
      message.actionType,
      message.parameters,
    );

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: "Confirm",
          conversationId: currentConversationId,
          language: language || "en",
          // GenUI: Generic action contract - backend resolves what is pending from conversation state
          action: "confirm",
          // Legacy: Keep confirmedEntity and forceIntent for backward compatibility during migration
          confirmedEntity: true,
          forceIntent: message.actionType,
          ...message.parameters,
        }),
      });

      const data = await response.json();
      
      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setLoading(false);
        return;
      }
      
      if (data.success) {
        console.log("[AIPane] Entity confirmation response:", {
          actionType: data.data.actionType,
          actionStatus: data.data.actionStatus,
          hasResult: !!data.data.result,
          hasSummary: !!data.data.result?.summary,
          summaryProjectNumber: data.data.result?.summary?.projectNumber,
          hasFieldMetadata: !!data.data.fieldMetadata,
        });

        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message || "Entity created successfully!",
          timestamp: new Date(),
          actionType: data.data.actionType,
          actionStatus: data.data.actionStatus || "completed", // Use actionStatus from backend if provided
          parameters: data.data.parameters,
          result: data.data.result,
          fieldMetadata: data.data.fieldMetadata,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("[AIPane] Entity confirmation error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Entity Edit Handler (re-enters clarification for a field)
  const handleEntityEdit = async (message: ChatMessage, field: string) => {
    console.log("[AIPane] Editing field:", field, "for message:", message.id);

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: `Edit ${field}`,
          conversationId: currentConversationId,
          language: language || "en",
          editField: field,
          forceIntent: message.actionType,
          ...message.parameters,
        }),
      });

      const data = await response.json();
      
      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setLoading(false);
        return;
      }
      
      if (data.success) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message,
          timestamp: new Date(),
          type: data.data.type,
          clarificationType: data.data.clarificationType,
          clarificationFieldType: data.data.clarificationFieldType,
          clarificationPlaceholder: data.data.clarificationPlaceholder,
          multiSelect: data.data.multiSelect,
          clarificationField: data.data.clarificationField,
          clarificationOptions: data.data.options,
          actionType: data.data.actionType,
          parameters: data.data.parameters,
          availableActions: data.data.availableActions,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Show the actual error message from backend
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "Failed to edit field",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("[AIPane] Entity edit error:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: error instanceof Error ? error.message : "Failed to edit field",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Plan Action Handler (approve/reject multi-step plans)
  const handlePlanAction = async (actionId: string) => {
    console.log("[AIPane] Plan action:", actionId);

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: actionId === "plan_approve" ? "Approve" : "Reject",
          conversationId: currentConversationId,
          language: language || "en",
          action: actionId,
        }),
      });

      const data = await response.json();
      
      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setLoading(false);
        return;
      }
      
      if (data.success) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message,
          timestamp: new Date(),
          type: data.data.type,
          actionType: data.data.actionType,
          actionStatus: data.data.actionStatus,
          parameters: data.data.parameters,
          result: data.data.result,
          fieldMetadata: data.data.fieldMetadata,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Show the actual error message from backend
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "Failed to process plan action",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("[AIPane] Plan action error:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: error instanceof Error ? error.message : "Failed to process plan action",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Recommendation Confirm Handler
  const handleRecommendationConfirm = async (message: ChatMessage) => {
    if (!message.recommendation) return;

    console.log("[AIPane] Confirming recommendation:", message.recommendation);

    const actionType =
      message.actionType || message.recommendation.suggestedAction.action;

    // For edit operations, use manual action instead of executing directly
    // Edit operations require user to specify what fields to update
    if (
      actionType === "edit_project" ||
      actionType === "edit_task" ||
      actionType === "edit_timesheet"
    ) {
      console.log("[AIPane] Edit operation detected, using manual action");
      const manualAction = message.availableActions?.[0]?.action;
      if (manualAction) {
        handleManualAction(
          manualAction,
          message.parameters ||
            message.recommendation.suggestedAction.parameters,
        );
        return;
      }
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      console.log("[AIPane] Confirming with actionType:", actionType);
      console.log(
        "[AIPane] Parameters:",
        message.recommendation.suggestedAction.parameters,
      );

      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: "Confirm", // Simple confirmation message
          conversationId: currentConversationId,
          language: language || "en",
          // GenUI: Generic action contract - backend resolves what is pending from conversation state
          action: "confirm",
          // Legacy: Keep confirmedRecommendation and forceIntent for backward compatibility during migration
          confirmedRecommendation: true,
          forceIntent: actionType,
          // Include all parameters from recommendation
          ...message.recommendation.suggestedAction.parameters,
        }),
      });

      const data = await response.json();

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Insufficient tokens. Please purchase more tokens to continue using AI features.",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
        setLoading(false);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return;
      }

      if (data.success) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message,
          timestamp: new Date(),
          actionId: data.data.actionId,
          actionType: data.data.actionType,
          actionStatus:
            data.data.type === "action_completed" ? "completed" : undefined,
          parameters: data.data.parameters,
          result: data.data.result,
          fieldMetadata: data.data.fieldMetadata,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Show the actual error message from backend
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Failed to confirm recommendation",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error("[AIPane] Error confirming recommendation:", error);
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error ? error.message : t("ai.error"),
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // Helper function to convert LinkedMessage data to RecommendationCard format
  const convertToRecommendation = (message: ChatMessage) => {
    if (!message.availableActions || message.availableActions.length === 0) {
      return null;
    }

    const primaryAction = message.availableActions[0];
    const actionType = primaryAction.action
      .replace("open_", "")
      .replace("_modal", "");

    // Extract entity names from detected entities for display
    const entityNames =
      message.detectedEntities?.map((e) => e.name).join(", ") || "";

    // Create a suggested action from the available action
    const suggestedAction = {
      action: message.actionType || actionType,
      parameters: message.parameters || {},
      confidence: 0.8, // Default confidence for manual actions
    };

    // Create alternatives from other available actions
    const alternatives = message.availableActions.slice(1).map((action) => ({
      label: action.label.replace(" Manually", ""),
      field: action.action.replace("open_", "").replace("_modal", ""),
      action: "modify" as const,
    }));

    // Create pattern explanation from detected entities
    const patternExplanation = entityNames
      ? `Detected entities: ${entityNames}`
      : "Ready to proceed with manual entry";

    return {
      suggestedAction,
      alternatives,
      patternExplanation,
      fieldMetadata: [], // Can be populated if needed
    };
  };

  // Recommendation Modify Handler
  const handleRecommendationModify = async (
    message: ChatMessage,
    field: string,
  ) => {
    console.log("[AIPane] Modifying recommendation field:", field);

    // Create a user message asking to change the field
    const modifyMessage = `Change ${field}`;
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: "user",
      content: modifyMessage,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");

      // Send message with current parameters but request clarification for the specific field
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: modifyMessage,
          conversationId: currentConversationId,
          language: language || "en",
          // Include existing parameters but remove the field to be modified
          ...Object.fromEntries(
            Object.entries(message.parameters || {}).filter(
              ([key]) => key !== field && key !== `${field}_id`,
            ),
          ),
        }),
      });

      const data = await response.json();

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Insufficient tokens. Please purchase more tokens to continue using AI features.",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
        setLoading(false);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return;
      }

      if (data.success) {
        const assistantMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.data.message,
          timestamp: new Date(),
          actionId: data.data.actionId,
          actionType: data.data.actionType,
          actionStatus: data.data.requiresConfirmation ? "pending" : undefined,
          requiresConfirmation: data.data.requiresConfirmation,
          parameters: data.data.parameters,
          type: data.data.type,
          clarificationType: data.data.clarificationType,
          clarificationField: data.data.clarificationField, // Store the actual field name
          clarificationFieldType: data.data.clarificationFieldType,
          clarificationPlaceholder: data.data.clarificationPlaceholder,
          multiSelect: data.data.multiSelect,
          clarificationOptions: data.data.options,
          detectedEntities: data.data.detectedEntities,
          availableActions: data.data.availableActions,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      } else {
        // Show the actual error message from backend
        const errorResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.message || "Failed to modify recommendation",
          role: "assistant",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorResponse]);
      }
    } catch (error) {
      console.error("[AIPane] Error modifying recommendation:", error);
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: error instanceof Error ? error.message : t("ai.error"),
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setLoading(false);
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  // Manual Action Handler - Opens relevant popups/modals
  const handleManualAction = (
    action: string,
    parameters?: Record<string, any>,
  ) => {
    console.log("[AI] Manual action requested:", action, parameters);

    /**
     * MANUAL ACTION FLOW:
     * 1. User clicks "Create [Entity] Manually" button in chat
     * 2. This function receives the action + pre-filled parameters
     * 3. Open the relevant modal with pre-filled data
     * 4. User completes remaining fields and saves
     * 5. Modal emits Socket.io event: 'entity_created'
     * 6. Chat listens for event and shows success message
     *
     * EXAMPLE INTEGRATION:
     *
     * // Open timesheet modal with pre-filled data:
     * dispatch(openTimesheetModal({
     *   prefilled: {
     *     staffId: parameters.staff_id,
     *     projectId: parameters.project_id,
     *     date: parameters.date,
     *     hours: parameters.hours
     *   },
     *   conversationId: currentConversationId // For feedback
     * }));
     *
     * // In modal component, after save:
     * socket.emit('entity_created', {
     *   type: 'timesheet',
     *   entityId: savedTimesheet._id,
     *   summary: `Timesheet for ${project.name} added on ${date} (${hours}h)`,
     *   conversationId: conversationId
     * });
     *
     * // Back in AIPane, listen for Socket event:
     * useEffect(() => {
     *   socket.on('entity_created', (data) => {
     *     if (data.conversationId === currentConversationId) {
     *       const successMessage = {
     *         id: Date.now().toString(),
     *         role: 'assistant' as const,
     *         content: `✅ ${data.summary}`,
     *         timestamp: new Date(),
     *         type: 'success'
     *       };
     *       setMessages(prev => [...prev, successMessage]);
     *     }
     *   });
     *   return () => { socket.off('entity_created'); };
     * }, [currentConversationId]);
     */

    switch (action) {
      case "open_timesheet_modal":
        handleOpenTimesheetModal(parameters);
        break;

      case "open_project_modal":
        handleOpenProjectModal(parameters);
        break;

      case "open_project_edit":
        // Dispatch event to open project form in edit mode
        if (parameters?.project_id) {
          window.dispatchEvent(
            new CustomEvent("openProjectFormEdit", {
              detail: {
                project_id: parameters.project_id,
              },
            }),
          );
        } else {
          console.warn("[AI] No project_id provided for edit action");
          alert("Project ID is required to edit a project");
        }
        break;

      case "open_task_modal":
        handleOpenTaskModal(parameters);
        break;

      case "open_staff_modal":
        handleOpenStaffModal(parameters);
        break;

      case "open_client_modal":
      case "open_contact_modal":
        handleOpenClientModal(parameters);
        break;

      case "open_sms_modal":
      case "open_email_modal":
        console.log("🔧 Open communication modal:", action);
        alert(
          `TODO: Open ${action.replace("open_", "").replace("_modal", "")} modal\n\nThis requires integration with the messaging system.`,
        );
        break;

      case "open_reports_page":
        window.location.href = "/reports";
        break;

      default:
        console.warn("[AI] Unknown manual action:", action);
        alert(`Unknown action: ${action}`);
    }
  };

  // Open Timesheet Form with pre-filled data
  const handleOpenTimesheetModal = (parameters?: Record<string, any>) => {
    console.log("[AI] 🎯 Opening timesheet form with parameters:", parameters);
    console.log("[AI] 📊 Parameter keys:", Object.keys(parameters || {}));

    const detail: any = {};

    // Pre-fill staff ID
    if (parameters?.staff_id) {
      console.log("[AI] ✅ Adding staffId:", parameters.staff_id);
      detail.staffId = parameters.staff_id;
    }

    // Pre-fill date (only if it's a valid date)
    if (parameters?.date) {
      let dateObj: Date | null = null;

      if (typeof parameters.date === "string") {
        // Try to parse the date string
        dateObj = new Date(parameters.date);

        // Check if the date is valid
        if (isNaN(dateObj.getTime())) {
          console.warn("[AI] Invalid date string, skipping:", parameters.date);
          dateObj = null;
        }
      } else if (parameters.date instanceof Date) {
        // Validate existing Date object
        if (!isNaN(parameters.date.getTime())) {
          dateObj = parameters.date;
        } else {
          console.warn("[AI] Invalid Date object, skipping");
        }
      }

      // Only set date if valid
      if (dateObj) {
        detail.date = dateObj;
      }
    }

    // 🔥 CRITICAL: Pass structured time entries from AI (if provided)
    if (parameters?.timeEntries && Array.isArray(parameters.timeEntries)) {
      detail.timeEntries = parameters.timeEntries;
      console.log(
        "[AI] 🕐 Passing time entries to modal:",
        parameters.timeEntries,
      );

      // Extract project/task from first entry to set as preselected
      const firstEntry = parameters.timeEntries[0];
      if (firstEntry?.projectId) {
        detail.projectId = firstEntry.projectId;
      }
      if (firstEntry?.taskId) {
        detail.taskId = firstEntry.taskId;
      }
    } else if (parameters?.project_id && parameters?.hours) {
      // Auto-create time entry from project + hours parameters
      // Calculate startTime and endTime based on hours
      const startTime = "08:00"; // Default start time
      const hours = parseFloat(parameters.hours) || 8;
      const totalMinutes = Math.floor(hours * 60);
      const endHour = 8 + Math.floor(totalMinutes / 60);
      const endMin = totalMinutes % 60;
      const endTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

      const timeEntry: any = {
        type: "work", // Required by TimesheetForm
        startTime: startTime,
        endTime: endTime,
        description:
          parameters.notes ||
          `${parameters.hours}h on ${parameters.project_name || "project"}`, // Optional notes
        materials: parameters.materials || [], // Materials for this entry
        equipment: parameters.equipment || [], // Equipment for this entry
      };

      detail.timeEntries = [timeEntry];

      // Set project/task as top-level props (required by TimesheetForm)
      detail.projectId = parameters.project_id;
      if (parameters.task_id) {
        detail.taskId = parameters.task_id;
      }

      console.log(
        "[AI] 🔧 Auto-created time entry from parameters:",
        timeEntry,
      );
    } else {
      // Fallback: just pre-select project/task for manual entry
      if (parameters?.project_id) {
        console.log(
          "[AI] ✅ Adding projectId (no hours, won't create entry):",
          parameters.project_id,
        );
        detail.projectId = parameters.project_id;
      }

      if (parameters?.task_id) {
        console.log("[AI] ✅ Adding taskId:", parameters.task_id);
        detail.taskId = parameters.task_id;
      }

      if (parameters?.hours) {
        console.log(
          "[AI] ✅ Adding hours (no project, won't create entry):",
          parameters.hours,
        );
        detail.hours = parameters.hours;
      }
    }

    console.log(
      "[AI] 🚀 Dispatching openTimesheetForm event with detail:",
      detail,
    );
    console.log("[AI] 📋 Detail keys:", Object.keys(detail));
    window.dispatchEvent(new CustomEvent("openTimesheetForm", { detail }));
  };

  // Open Project Form with pre-filled data
  const handleOpenProjectModal = (parameters?: Record<string, any>) => {
    console.log("[AI] Opening project form with parameters:", parameters);

    // Map parameters to form field names
    // Handle multiple possible parameter key formats
    const clientId = parameters?.client_id || parameters?.clientId;
    const projectName =
      parameters?.project_name || parameters?.name || parameters?.projectName;
    const startDate =
      parameters?.start_date || parameters?.startDate || parameters?.date;
    const description = parameters?.description;
    const siteAddress = parameters?.site_address || parameters?.siteAddress;
    const constructionTypes =
      parameters?.construction_types || parameters?.constructionTypes;
    const projectType = parameters?.project_type || parameters?.projectType;

    // Dispatch custom event for Projects component to handle
    window.dispatchEvent(
      new CustomEvent("openProjectForm", {
        detail: {
          client_id: clientId,
          name: projectName,
          start_date: startDate,
          description: description,
          site_address: siteAddress,
          construction_types: constructionTypes,
          project_type: projectType,
        },
      }),
    );

    console.log("[AI] Project form event dispatched with all parameters");
  };

  // Open Task Form with pre-filled data
  const handleOpenTaskModal = (parameters?: Record<string, any>) => {
    console.log("[AI] Opening task form with parameters:", parameters);

    window.dispatchEvent(
      new CustomEvent("openTaskForm", {
        detail: {
          project_id: parameters?.project_id,
          task_name: parameters?.task_name,
          description: parameters?.description,
          assigned_to: parameters?.assigned_to,
        },
      }),
    );

    console.log("[AI] Task form event dispatched");
  };

  // Open Staff Form with pre-filled data
  const handleOpenStaffModal = (parameters?: Record<string, any>) => {
    console.log("[AI] Opening staff form with parameters:", parameters);

    window.dispatchEvent(
      new CustomEvent("openStaffForm", {
        detail: {
          first_name: parameters?.first_name,
          last_name: parameters?.last_name,
          email: parameters?.email,
          phone: parameters?.phone,
        },
      }),
    );

    console.log("[AI] Staff form event dispatched");
  };

  // Open Client/Contact Form with pre-filled data
  const handleOpenClientModal = (parameters?: Record<string, any>) => {
    console.log("[AI] Opening client form with parameters:", parameters);

    window.dispatchEvent(
      new CustomEvent("openContactForm", {
        detail: {
          name: parameters?.client_name,
          email: parameters?.email,
          phone: parameters?.phone,
          company: parameters?.company,
        },
      }),
    );

    console.log("[AI] Client form event dispatched");
  };

  // Demo Prompts Handler - Auto-send for instant testing
  const handleDemoPromptSelect = (prompt: string) => {
    // Set input and immediately send
    const sendPrompt = async () => {
      setInputValue(prompt);
      // Wait a tick for state to update
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Manually trigger send using the prompt directly
      if (!prompt.trim() || loading) return;

      const userMessage = prompt;
      const previousMessages = messages;

      // Add user message immediately
      const newMessages = [
        ...previousMessages,
        {
          id: Date.now().toString(),
          role: "user" as const,
          content: userMessage,
          timestamp: new Date(),
        },
      ];
      setMessages(newMessages);
      setInputValue("");
      setLoading(true);

      // Rest of sending logic...
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/mcp/chat`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: userMessage,
            conversationId: currentConversationId,
            language: language || "en",
          }),
        });

      // Parse response JSON
      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If JSON parsing fails, check if it's a 402 error
        if (response.status === 402) {
          data = {
            success: false,
            code: 'INSUFFICIENT_TOKENS',
            message: 'Insufficient tokens. Please purchase more tokens to continue using AI features.'
          };
        } else {
          throw parseError;
        }
      }

      // Check for token errors (402 status) - this will automatically open the purchase modal
      const isTokenError = handleTokenError(response, data);
      
      if (isTokenError || response.status === 402) {
        // Ensure modal is opened (handleTokenError should have opened it, but ensure it's open)
        if (!isTokenError && response.status === 402) {
          openPurchaseModal();
        }
        
        // Show error message in chat
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "Insufficient tokens. Please purchase more tokens to continue using AI features.",
          timestamp: new Date(),
        };
        setMessages([...newMessages, errorMessage]);
        setLoading(false);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
        return;
      }

      if (data.success) {
        // Trigger AI Architect Panel in dev mode
        // Note: AI Architect Panel is now accessible from the header

        console.log("[AIPane] Backend response data:", data.data);
          console.log("[AIPane] availableActions:", data.data.availableActions);

          console.log(
            "[AIPane] Creating clarification message with availableActions:",
            data.data.availableActions,
          );

          const assistantMessage: ChatMessage = {
            id: (Date.now() + 1).toString(),
            role: "assistant",
            content: data.data.message,
            timestamp: new Date(),
            actionId: data.data.actionId,
            actionType: data.data.actionType,
            actionStatus: data.data.requiresConfirmation
              ? "pending"
              : "completed",
            requiresConfirmation: data.data.requiresConfirmation,
            parameters: data.data.parameters,
            // Clarification data
            type: data.data.type,
            clarificationType: data.data.clarificationType,
            clarificationOptions: data.data.options,
            // Entity linking and actions
            detectedEntities: data.data.detectedEntities,
            availableActions: data.data.availableActions,
            // Plan approval (multi-step workflows)
            plan: data.data.plan,
            actions: data.data.actions,
            requiresUserAction: data.data.requiresUserAction,
          };

          console.log("[AIPane] Final assistantMessage object:", {
            type: assistantMessage.type,
            clarificationType: assistantMessage.clarificationType,
            availableActions: assistantMessage.availableActions,
            plan: assistantMessage.plan,
            actions: assistantMessage.actions,
            hasPlanData: !!assistantMessage.plan && !!assistantMessage.actions,
          });

          setMessages([...newMessages, assistantMessage]);

          if (!currentConversationId && data.data.conversationId) {
            setCurrentConversationId(data.data.conversationId);
          }
        } else {
        // Show the actual error message from backend
        const errorMessage: ChatMessage = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.message || "Failed to get response",
          timestamp: new Date(),
        };
        setMessages([...newMessages, errorMessage]);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: error instanceof Error ? error.message : t("ai.error"),
        timestamp: new Date(),
      };
      setMessages([...newMessages, errorMessage]);
      } finally {
        setLoading(false);
        // Scroll to messages after sending
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    };

    sendPrompt();
  };

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const historyPanelRef = useRef<HTMLDivElement>(null);
  const { canDeleteAll, canDeleteOwn } = usePermissions();
  const { user } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Account for admin banner at top (44px)
  const isSystemAdmin = user?.role === "System Administrator";
  const showAdminBanner = isSystemAdmin && !user?.impersonating;
  const showImpersonationBanner = user?.impersonating;
  const topOffset = showAdminBanner || showImpersonationBanner ? "44px" : "0";

  // Detect sidebar collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });

  // Listen for sidebar toggle events
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem("sidebarCollapsed");
      setSidebarCollapsed(saved === "true");
    };

    // Listen for custom event when sidebar toggles
    window.addEventListener("sidebarToggle", handleStorageChange);
    // Also listen for storage events (in case of multiple tabs)
    window.addEventListener("storage", handleStorageChange);

    // Poll localStorage periodically as fallback
    const interval = setInterval(() => {
      const saved = localStorage.getItem("sidebarCollapsed");
      const currentState = saved === "true";
      if (currentState !== sidebarCollapsed) {
        setSidebarCollapsed(currentState);
      }
    }, 100);

    return () => {
      window.removeEventListener("sidebarToggle", handleStorageChange);
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [sidebarCollapsed]);

  const sidebarLeftOffset = sidebarCollapsed ? "80px" : "256px";

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Define functions that are used in hooks - must be before useEffect that uses them
  const loadConversations = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${apiUrl}/api/ai/conversations`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setConversations(data.data);
      }
    } catch (error) {
      console.error("Failed to load conversations:", error);
    }
  }, [apiUrl]);

  const loadConversation = useCallback(
    async (conversationId: string) => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${apiUrl}/api/ai/conversations/${conversationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await response.json();
        if (data.success) {
          setCurrentConversationId(data.data.id);
          const loadedMessages = data.data.messages.map((msg: any, index: number) => {
            // Check if this is a clarification message that was answered
            // A clarification is answered if the next message is from the user
            const isAnswered = 
              (msg.type === "clarification_needed" || msg.type === "optional_fields_prompt") &&
              msg.clarificationOptions &&
              index < data.data.messages.length - 1 &&
              data.data.messages[index + 1].role === "user";

            return {
              id: msg.id,
              content: msg.content,
              role: msg.role,
              timestamp: new Date(msg.timestamp),
              // Map all rich fields
              actionId: msg.actionId,
              actionType: msg.actionType,
              actionStatus: msg.actionStatus,
              requiresConfirmation: msg.requiresConfirmation,
              parameters: msg.parameters,
              result: msg.result,
              type: msg.type,
              clarificationType: msg.clarificationType,
              clarificationField: msg.clarificationField,
              clarificationOptions: msg.clarificationOptions,
              clarificationAnswered: msg.clarificationAnswered || isAnswered,
              fieldMetadata: msg.fieldMetadata,
              detectedEntities: msg.detectedEntities,
              availableActions: msg.availableActions,
              projectPreview: msg.projectPreview,
              recommendation: msg.recommendation,
              searchResults: msg.searchResults,
              plan: msg.plan,
              actions: msg.actions,
              requiresUserAction: msg.requiresUserAction,
            };
          });
          setMessages(loadedMessages);
          // Don't close history panel - let it persist
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);
      } finally {
        setLoading(false);
      }
    },
    [apiUrl],
  );

  // Load conversations when component mounts and restore last conversation
  useEffect(() => {
    const initializeChat = async () => {
      // Restore history panel state from localStorage
      const savedHistoryState = localStorage.getItem("aiChatHistoryOpen");
      if (savedHistoryState === "true") {
        setShowConversations(true);
      }

      // Load conversations list
      await loadConversations();

      // Try to restore last conversation from localStorage
      const savedConversationId = localStorage.getItem(
        "aiChatLastConversationId",
      );
      if (savedConversationId) {
        // Verify the conversation still exists before loading
        const token = localStorage.getItem("token");
        try {
          const response = await fetch(
            `${apiUrl}/api/ai/conversations/${savedConversationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
          const data = await response.json();
          if (data.success) {
            // Conversation exists, load it
            hasAttemptedAutoLoad.current = true; // Mark as attempted so auto-load doesn't run
            await loadConversation(savedConversationId);
            return;
          }
        } catch (error) {
          console.error("Failed to verify saved conversation:", error);
        }
        // Conversation doesn't exist, clear saved ID
        localStorage.removeItem("aiChatLastConversationId");
      }

      // If no saved conversation, the auto-load effect will load the most recent one
    };

    initializeChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load most recent conversation if none is active
  useEffect(() => {
    // Only auto-load once, if:
    // 1. We haven't attempted auto-load yet
    // 2. Conversations are loaded
    // 3. No current conversation is active
    // 4. Messages are empty (meaning no conversation loaded yet)
    if (
      !hasAttemptedAutoLoad.current &&
      conversations.length > 0 &&
      !currentConversationId &&
      messages.length === 0
    ) {
      hasAttemptedAutoLoad.current = true;
      // Load the most recent conversation (first in the sorted list)
      const mostRecentConversation = conversations[0];
      if (mostRecentConversation) {
        loadConversation(mostRecentConversation.id);
      }
    } else if (
      !hasAttemptedAutoLoad.current &&
      conversations.length === 0 &&
      !currentConversationId &&
      messages.length === 0
    ) {
      hasAttemptedAutoLoad.current = true;
      // No conversations exist, show greeting
      setMessages([
        {
          id: "1",
          content: t("ai.greeting"),
          role: "assistant",
          timestamp: new Date(),
        },
      ]);
    }
  }, [conversations, currentConversationId, messages, t, loadConversation]);

  // Save current conversation ID to localStorage when it changes
  useEffect(() => {
    if (currentConversationId) {
      localStorage.setItem("aiChatLastConversationId", currentConversationId);
    } else {
      localStorage.removeItem("aiChatLastConversationId");
    }
  }, [currentConversationId]);

  // Save history panel state to localStorage
  useEffect(() => {
    localStorage.setItem("aiChatHistoryOpen", showConversations.toString());
  }, [showConversations]);

  // Check if Buddy AI feature is enabled - must be after all hooks
  const featureEnabled = isFeatureEnabled("buddyAI");

  const startNewConversation = () => {
    setCurrentConversationId(null);
    setInputValue("");
    setMessages([
      {
        id: "1",
        content: t("ai.greeting"),
        role: "assistant",
        timestamp: new Date(),
      },
    ]);
    // Clear saved conversation ID when starting new conversation
    localStorage.removeItem("aiChatLastConversationId");
    // Scroll to top
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
    // Don't close history panel - let it persist
  };

  const deleteConversation = async (
    conversationId: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();

    const conversation = conversations.find((c) => c.id === conversationId);
    if (!conversation) return;

    const canDeleteThis = conversation.isOwn
      ? canDeleteOwn("Buddy AI")
      : canDeleteAll("Buddy AI");

    if (!canDeleteThis) {
      return; // Button should be hidden, but guard against edge cases
    }

    setConversationToDelete(conversationId);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!conversationToDelete) return;

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${apiUrl}/api/ai/conversations/${conversationToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();
      if (data.success) {
        setConversations((prev) =>
          prev.filter((c) => c.id !== conversationToDelete),
        );
        if (currentConversationId === conversationToDelete) {
          startNewConversation();
        }
      } else {
        alert(data.message || "Failed to delete conversation");
      }
    } catch (error) {
      console.error("Failed to delete conversation:", error);
      alert("Failed to delete conversation");
    } finally {
      setShowDeleteConfirm(false);
      setConversationToDelete(null);
    }
  };

  const clearCurrentConversation = () => {
    setShowClearConfirm(true);
  };

  const handleClearConfirm = () => {
    setMessages([
      {
        id: "1",
        content: t("ai.greeting"),
        role: "assistant",
        timestamp: new Date(),
      },
    ]);
    setCurrentConversationId(null);
    setShowClearConfirm(false);
  };

  // Don't close history panel on outside click - let it persist

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      content: userMessage,
      role: "user",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInputValue("");
    setLoading(true);

    // 🔄 Add "thinking" indicator message
    const thinkingMessageId = (Date.now() + 1).toString();
    const thinkingMessage: ChatMessage = {
      id: thinkingMessageId,
      content: "", // Will be updated as we process
      role: "system",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, thinkingMessage]);

    // Helper to update thinking message
    const updateThinking = (text: string) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === thinkingMessageId ? { ...msg, content: text } : msg,
        ),
      );
    };

    updateThinking("🔍 Analyzing your request...");

    // Trigger AI Architect Panel in dev mode
    // Note: AI Architect Panel is now accessible from the header

    try {
      updateThinking("🤖 Classifying intent and extracting entities...");

      const token = localStorage.getItem("token");

      // Prepare request - use FormData if files are attached, otherwise JSON
      let requestBody: FormData | string;
      let headers: HeadersInit;

      if (selectedFiles.length > 0) {
        const formData = new FormData();
        formData.append("message", userMessage);
        if (currentConversationId) {
          formData.append("conversationId", currentConversationId);
        }
        formData.append("language", language || "en");

        selectedFiles.forEach((file) => {
          formData.append("files", file);
        });

        requestBody = formData;
        headers = {
          Authorization: `Bearer ${token}`,
          // Don't set Content-Type - browser will set it with boundary
        };
      } else {
        requestBody = JSON.stringify({
          message: userMessage,
          conversationId: currentConversationId || undefined,
          language: language || "en",
        });
        headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };
      }

      // Use MCP endpoint instead of regular AI chat
      const response = await fetch(`${apiUrl}/api/mcp/chat`, {
        method: "POST",
        headers,
        body: requestBody,
      });

      // Clear selected files after sending
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      const data = await response.json();

      // Update thinking based on response type
      if (data.data?.type === "clarification_needed") {
        updateThinking("❓ Checking for missing information...");
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else if (data.data?.type === "action_pending") {
        updateThinking("⚙️ Preparing to execute action...");
        await new Promise((resolve) => setTimeout(resolve, 300));
      } else {
        updateThinking("✅ Processing complete");
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // ✅ KEEP thinking message visible (don't remove it)

      if (data.success) {
        const aiResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          content: data.data.message,
          role: "assistant",
          timestamp: new Date(),
          // MCP enhancements
          actionId: data.data.actionId,
          actionType: data.data.actionType,
          actionStatus:
            data.data.actionStatus || // Use actionStatus from backend if provided
            (data.data.type === "action_pending"
              ? "pending"
              : data.data.type === "action_completed"
                ? "completed"
                : undefined),
          requiresConfirmation: data.data.requiresConfirmation,
          parameters: data.data.parameters,
          // 🔥 CRITICAL FIX: Add clarification fields from backend response
          type:
            data.data.type === "clarification_needed"
              ? "clarification_needed"
              : data.data.type === "recommendation"
                ? "recommendation"
                : data.data.type === "entity_confirmation"
                  ? "entity_confirmation"
                  : data.data.type === "optional_fields_prompt"
                    ? "optional_fields_prompt"
                    : data.data.type === "plan_approval_request"
                      ? "plan_approval_request"
                      : data.data.type === "plan_rejected"
                        ? "plan_rejected"
                        : data.data.type === "action_result"
                          ? undefined // action_result is handled via actionStatus, not type
                          : undefined,
          clarificationType: data.data.clarificationType,
          clarificationFieldType: data.data.clarificationFieldType,
          clarificationPlaceholder: data.data.clarificationPlaceholder,
          multiSelect: data.data.multiSelect,
          clarificationOptions: data.data.options, // ← This was missing!
          clarificationField: data.data.clarificationField,
          fieldMetadata: data.data.fieldMetadata,
          detectedEntities: data.data.detectedEntities,
          availableActions: data.data.availableActions,
          // Project preview for project creation
          projectPreview: data.data.projectPreview,
          // Recommendation fields
          recommendation: data.data.recommendation,
          // GenUI: Display metadata and render hints from backend
          display: data.data.display,
          renderAs: data.data.renderAs,
          // Global search results - check multiple possible locations
          searchResults: (() => {
            // First check if searchResults is directly provided
            if (
              data.data.searchResults &&
              Array.isArray(data.data.searchResults)
            ) {
              return data.data.searchResults;
            }
            // Then check result.results
            if (
              data.data.result?.results &&
              Array.isArray(data.data.result.results)
            ) {
              return data.data.result.results;
            }
            // Then check result.data.results
            if (
              data.data.result?.data?.results &&
              Array.isArray(data.data.result.data.results)
            ) {
              return data.data.result.data.results;
            }
            return undefined;
          })(),
          // Project summary data
          result: data.data.result,
          // Plan approval (multi-step workflows)
          plan: data.data.plan,
          actions: data.data.actions,
          requiresUserAction: data.data.requiresUserAction,
        };

        // Debug logging for completed actions
        if (aiResponse.actionStatus === "completed") {
          console.log("[AIPane] ✅ Received completed action:", {
            actionType: aiResponse.actionType,
            actionStatus: aiResponse.actionStatus,
            hasResult: !!aiResponse.result,
            hasSummary: !!aiResponse.result?.summary,
            summaryProjectNumber: aiResponse.result?.summary?.projectNumber,
            hasFieldMetadata: !!aiResponse.fieldMetadata,
            fieldMetadataLength: aiResponse.fieldMetadata?.length || 0,
            resultKeys: aiResponse.result ? Object.keys(aiResponse.result) : [],
          });
        }

        // Debug logging for get_project_summary
        if (data.data.actionType === "get_project_summary") {
          console.log("[AIPane] 🐛 DEBUG get_project_summary message:", {
            actionType: aiResponse.actionType,
            actionStatus: aiResponse.actionStatus,
            hasResult: !!aiResponse.result,
            resultKeys: aiResponse.result ? Object.keys(aiResponse.result) : [],
            hasProjectNumber: aiResponse.result?.projectNumber,
            result: aiResponse.result,
          });
        }

        // Check if this is a walkthrough trigger
        if (data.data.actionType === "start_walkthrough") {
          const walkthroughId =
            data.data.result?.walkthroughId || data.data.data?.walkthroughId;
          if (walkthroughId) {
            const walkthroughConfig = getWalkthrough(walkthroughId);
            if (walkthroughConfig) {
              // Set translation function for walkthrough
              (window as any).__t__ = t;
              // Start the walkthrough
              setTimeout(() => {
                startWalkthrough(walkthroughConfig);
              }, 500);
            }
          }
        }

        setMessages((prev) => [...prev, aiResponse]);

        // Update conversation ID if this was a new conversation
        if (data.data.conversationId && !currentConversationId) {
          setCurrentConversationId(data.data.conversationId);
          // Reload conversations to get the new one
          loadConversations();
        }
      } else {
        throw new Error(data.message || "Failed to get AI response");
      }
    } catch (error) {
      console.error("AI chat error:", error);

      // Update thinking message to show error
      updateThinking(
        "❌ Error occurred - " +
          (error instanceof Error ? error.message : "Unknown error"),
      );

      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        content: t("ai.error"),
        role: "assistant",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Clear Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showClearConfirm}
        onCancel={() => setShowClearConfirm(false)}
        onConfirm={handleClearConfirm}
        title={t("ai.clear")}
        message={t("ai.clearConfirm")}
        confirmLabel={t("ai.clear")}
        cancelLabel={t("common.cancel")}
        type="info"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setConversationToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title={t("ai.deleteConversation")}
        message={t("ai.deleteConversationConfirm")}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        type="danger"
      />

      {/* History Panel - Pop out */}
      {featureEnabled && showConversations && (
        <div
          ref={historyPanelRef}
          className={`fixed bottom-0 w-80 bg-white dark:bg-gray-800 ${isRTL ? "border-r" : "border-l"} border-gray-200 dark:border-gray-700 shadow-2xl z-50 flex flex-col`}
          style={{
            ...(isRTL
              ? {
                  right: isExpanded ? sidebarLeftOffset : undefined,
                  left: isExpanded ? undefined : "448px",
                }
              : {
                  left: isExpanded ? sidebarLeftOffset : undefined,
                  right: isExpanded ? undefined : "448px",
                }),
            top: topOffset,
          }}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {t("ai.conversationHistory")}
            </h3>
            <button
              onClick={() => setShowConversations(false)}
              className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label={t("ai.closeHistory")}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={startNewConversation}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors text-sm font-medium"
              aria-label={t("ai.newConversation")}
            >
              <Plus className="w-4 h-4" />
              <span>{t("ai.newConversation")}</span>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                {t("ai.noConversations")}
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {conversations.map((conv) => {
                  const canDeleteThis = conv.isOwn
                    ? canDeleteOwn("Buddy AI")
                    : canDeleteAll("Buddy AI");
                  return (
                    <div
                      key={conv.id}
                      onClick={() => loadConversation(conv.id)}
                      className={`p-3 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                        currentConversationId === conv.id
                          ? "bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                          : ""
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {conv.title}
                            </p>
                            {!conv.isOwn && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                                {conv.userName}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatDateTime(conv.lastMessageAt)}
                          </p>
                        </div>
                        {canDeleteThis && (
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="ml-2 p-1 text-gray-400 hover:text-error-500 transition-colors"
                            aria-label={t("ai.deleteConversationLabel")}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Desktop Pane */}
      <div
        className={`hidden md:flex fixed ${isExpanded ? "bottom-0" : isRTL ? "left-16 bottom-0 w-96" : "right-16 bottom-0 w-96"} bg-white dark:bg-gray-800 ${isRTL ? "border-r" : "border-l"} border-gray-200 dark:border-gray-700 shadow-lg flex-col z-30`}
        style={{
          top: topOffset,
          ...(isRTL
            ? {
                right: isExpanded
                  ? showConversations
                    ? `calc(${sidebarLeftOffset} + 320px)`
                    : sidebarLeftOffset
                  : undefined,
                left: isExpanded ? "64px" : undefined,
              }
            : {
                left: isExpanded
                  ? showConversations
                    ? `calc(${sidebarLeftOffset} + 320px)`
                    : sidebarLeftOffset
                  : undefined,
                right: isExpanded ? "64px" : "64px",
              }),
          width: isExpanded ? undefined : undefined, // Width is calculated by left and right when expanded
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {t("ai.title")}
            </h3>
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            {featureEnabled && (
              <>
                <button
                  onClick={startNewConversation}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={t("ai.newConversation")}
                  title={t("ai.newConversation")}
                >
                  <Plus className="w-5 h-5" />
                </button>
                <button
                  onClick={clearCurrentConversation}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={t("ai.clear")}
                  title={t("ai.clear")}
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowConversations(!showConversations)}
                  className={`p-2 rounded-lg transition-colors ${
                    showConversations
                      ? "bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                  aria-label={t("ai.conversations")}
                  title={t("ai.conversations")}
                >
                  <Clock className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={isExpanded ? "Minimize" : "Expand"}
                  title={
                    isExpanded ? t("ai.minimize") : t("ai.expandToFullScreen")
                  }
                >
                  {isExpanded ? (
                    <Minimize2 className="w-5 h-5" />
                  ) : (
                    <Maximize2 className="w-5 h-5" />
                  )}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close AI chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Voice Visualizer - Desktop */}
        {featureEnabled &&
          voiceConnected &&
          (voiceRecording || voiceSpeaking) && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {voiceRecording
                    ? t("ai.voiceListening")
                    : t("ai.voiceSpeaking")}
                </span>
                <button
                  onClick={voiceDisconnect}
                  className="text-xs text-gray-500 hover:text-error-600 dark:text-gray-400 dark:hover:text-error-400"
                >
                  {t("common.disconnect")}
                </button>
              </div>
              <VoiceVisualizer
                isActive={true}
                type={voiceRecording ? "user" : "assistant"}
              />
            </div>
          )}

        {/* Demo Prompts (Dev Mode Only) */}
        {isDevelopment && featureEnabled && (
          <DemoPrompts onSelectPrompt={handleDemoPromptSelect} />
        )}

        {/* Messages or Coming Soon */}
        {!featureEnabled ? (
          <div className="flex-1 flex items-center justify-center">
            <ComingSoon
              featureName="Buddy AI Chat"
              description="AI-powered chat assistant for project management and business insights"
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading && (
              <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                {t("ai.loadingConversation")}
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : message.role === "system" ? "justify-center" : "justify-start"}`}
              >
                {/* System "thinking" messages */}
                {message.role === "system" ? (
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 italic py-1 px-2">
                    {message.content}
                  </div>
                ) : message.type === "recommendation" &&
                  message.recommendation &&
                  message.recommendation.suggestedAction ? (
                  <div className="max-w-[85%] w-full">
                    <RecommendationCard
                      message={message.content}
                      recommendation={message.recommendation}
                      parameters={message.parameters || {}}
                      onConfirm={() => handleRecommendationConfirm(message)}
                      onModify={(field) =>
                        handleRecommendationModify(message, field)
                      }
                      onManual={() =>
                        handleManualAction(
                          "open_" +
                            (message.actionType?.replace("create_", "") ||
                              "timesheet") +
                            "_modal",
                          message.parameters,
                        )
                      }
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.type === "plan_approval_request" &&
                  message.plan &&
                  message.actions ? (
                  <>
                    {console.log(
                      "[AIPane] 🎯 Rendering PlanApprovalCard for message:",
                      message.id,
                      {
                        type: message.type,
                        hasPlan: !!message.plan,
                        hasActions: !!message.actions,
                        planSteps: message.plan?.steps?.length,
                        actionsCount: message.actions?.length,
                      },
                    )}
                    <div className="max-w-[85%] w-full">
                      <PlanApprovalCard
                        message={message.content}
                        plan={message.plan}
                        actions={message.actions}
                        onAction={handlePlanAction}
                        isDark={isDarkMode}
                      />
                      <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </>
                ) : message.type === "entity_confirmation" ? (
                  <div className="max-w-[85%] w-full">
                    <EntityConfirmationCard
                      message={message.content}
                      parameters={message.parameters || {}}
                      fieldMetadata={message.fieldMetadata}
                      actionType={message.actionType || ""}
                      onConfirm={() => handleEntityConfirmation(message)}
                      onEdit={(field) => handleEntityEdit(message, field)}
                      onManual={() =>
                        handleManualAction(
                          message.availableActions?.[0]?.action ||
                            "open_project_modal",
                          message.parameters,
                        )
                      }
                      isDark={isDarkMode}
                      availableActions={message.availableActions}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (message.type === "clarification_needed" ||
                    message.type === "optional_fields_prompt") &&
                  message.clarificationOptions ? (
                  <div className="max-w-[85%] w-full">
                    <RecommendOptions
                      message={message.content}
                      clarificationType={message.clarificationType}
                      clarificationField={message.clarificationField}
                      clarificationFieldType={message.clarificationFieldType}
                      clarificationPlaceholder={
                        message.clarificationPlaceholder
                      }
                      multiSelect={message.multiSelect}
                      options={message.clarificationOptions || []}
                      onSelect={(option) =>
                        handleClarificationSelect(option, message.id)
                      }
                      isDark={isDarkMode}
                      availableActions={message.availableActions}
                      onManualAction={(action) =>
                        handleManualAction(action, message.parameters)
                      }
                      isCollapsed={message.clarificationAnswered}
                      display={message.display}
                      isOptionalPrompt={message.type === "optional_fields_prompt"}
                    />
                    {!message.clarificationAnswered && (
                      <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ) : message.projectPreview ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectPreviewMessage
                      message={message.content}
                      projectPreview={message.projectPreview}
                      onConfirm={() => {
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === message.id
                              ? {
                                  ...msg,
                                  content: `${msg.content}\n\n✅ ${t("project.createdSuccessfully")}`,
                                }
                              : msg,
                          ),
                        );
                      }}
                      onEdit={() => {
                        window.location.href = `/projects/${message.projectPreview?.projectId}`;
                      }}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (() => {
                    // Debug logging for get_project_summary rendering
                    if (message.actionType === "get_project_summary") {
                      console.log(
                        "[AIPane] 🐛 DEBUG get_project_summary rendering check:",
                        {
                          actionType: message.actionType,
                          hasResult: !!message.result,
                          resultType: typeof message.result,
                          resultKeys: message.result
                            ? Object.keys(message.result)
                            : [],
                          hasProjectNumber: message.result?.projectNumber,
                          projectNumber: message.result?.projectNumber,
                          conditionMatch:
                            message.actionType === "get_project_summary" &&
                            message.result &&
                            message.result.projectNumber,
                        },
                      );
                    }
                    return (
                      message.actionType === "get_project_summary" &&
                      message.result &&
                      message.result.projectNumber
                    );
                  })() ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectSummaryCard
                      message={message.content}
                      summary={message.result}
                      projectId={message.parameters?.project_id}
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.searchResults &&
                  Array.isArray(message.searchResults) &&
                  message.searchResults.length > 0 ? (
                  <div className="max-w-[85%] w-full">
                    <SearchResultCard
                      results={message.searchResults}
                      query={message.content}
                      onResultClick={(result) => {
                        if (result.url) {
                          window.location.href = result.url;
                        }
                      }}
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.role === "assistant" &&
                  (message.detectedEntities ||
                    (message.availableActions &&
                      message.availableActions.length > 0)) ? (
                  (() => {
                    // CRITICAL: Check for get_project_summary FIRST before any other conditions
                    if (
                      message.actionType === "get_project_summary" &&
                      message.result &&
                      message.result.projectNumber
                    ) {
                      console.log(
                        "[AIPane] 🐛 DEBUG: Rendering ProjectSummaryCard from detectedEntities block",
                      );
                      return (
                        <div className="max-w-[85%] w-full">
                          <ProjectSummaryCard
                            message={message.content}
                            summary={message.result}
                            projectId={message.parameters?.project_id}
                            isDark={isDarkMode}
                          />
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }

                    // CRITICAL: Don't show recommendation card for completed actions
                    // Only show for clarification/recommendation scenarios
                    if (message.actionStatus === "completed") {
                      console.log("[AIPane] Rendering completed action:", {
                        actionType: message.actionType,
                        actionStatus: message.actionStatus,
                        hasResult: !!message.result,
                        hasSummary: !!message.result?.summary,
                        summaryProjectNumber:
                          message.result?.summary?.projectNumber,
                        hasFieldMetadata: !!message.fieldMetadata,
                        fieldMetadataLength: message.fieldMetadata?.length || 0,
                      });

                      // PRIORITY 1: Check if this is a project creation with summary data - show ProjectSummaryCard
                      if (
                        message.actionType === "create_project" &&
                        message.result &&
                        message.result.summary &&
                        message.result.summary.projectNumber
                      ) {
                        console.log(
                          "[AIPane] ✅ Rendering ProjectSummaryCard for create_project",
                        );
                        return (
                          <div className="max-w-[85%] w-full">
                            <ProjectSummaryCard
                              message={message.content}
                              summary={message.result.summary}
                              projectId={
                                message.result.projectId?.toString() ||
                                message.result.projectId
                              }
                              isDark={isDarkMode}
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      }

                      // PRIORITY 2: Check if this is a project summary request
                      if (
                        message.actionType === "get_project_summary" &&
                        message.result &&
                        message.result.projectNumber
                      ) {
                        return (
                          <div className="max-w-[85%] w-full">
                            <ProjectSummaryCard
                              message={message.content}
                              summary={message.result}
                              projectId={message.parameters?.project_id}
                              isDark={isDarkMode}
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      }

                      // PRIORITY 3: Check if fieldMetadata is present - show EntityConfirmationCard instead of plain text
                      if (
                        message.fieldMetadata &&
                        Array.isArray(message.fieldMetadata) &&
                        message.fieldMetadata.length > 0 &&
                        message.parameters
                      ) {
                        console.log(
                          "[AIPane] ✅ Rendering EntityConfirmationCard with",
                          message.fieldMetadata.length,
                          "fields",
                        );
                        return (
                          <div className="max-w-[85%] w-full">
                            <EntityConfirmationCard
                              message={message.content}
                              parameters={message.parameters || {}}
                              fieldMetadata={message.fieldMetadata}
                              actionType={message.actionType || ""}
                              onConfirm={() => {}} // No action needed on success
                              onEdit={() => {}} // No edit needed on success
                              onManual={() => {}} // No manual action needed on success
                              isDark={isDarkMode}
                              availableActions={[]} // No actions needed on success
                              showActions={false} // Hide action buttons on success
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      } else {
                        console.log(
                          "[AIPane] ⚠️ Not showing EntityConfirmationCard - missing fieldMetadata or parameters",
                        );
                      }
                      // Show success message for other completed actions
                      return (
                        <div className="max-w-[85%] w-full">
                          <div
                            className={`rounded-lg p-3 ${
                              isDarkMode
                                ? "bg-success-900/20 border border-green-800/50"
                                : "bg-green-50 border border-green-200"
                            }`}
                          >
                            <p
                              className={`text-sm ${isDarkMode ? "text-success-300" : "text-success-800"}`}
                            >
                              ✅ {message.content}
                            </p>
                          </div>
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }

                    const recommendation = convertToRecommendation(message);
                    if (
                      recommendation &&
                      message.availableActions &&
                      message.availableActions.length > 0
                    ) {
                      return (
                        <div className="max-w-[85%] w-full">
                          <RecommendationCard
                            message={message.content}
                            recommendation={recommendation}
                            parameters={message.parameters || {}}
                            onConfirm={() => {
                              // When confirmed, trigger the primary manual action
                              const primaryAction =
                                message.availableActions![0];
                              handleManualAction(
                                primaryAction.action,
                                message.parameters,
                              );
                            }}
                            onModify={(field) => {
                              // Find the action for this field and trigger it
                              const action = message.availableActions?.find(
                                (a) => a.action.includes(field),
                              );
                              if (action) {
                                handleManualAction(
                                  action.action,
                                  message.parameters,
                                );
                              }
                            }}
                            onManual={() => {
                              // Trigger the primary manual action
                              const primaryAction =
                                message.availableActions![0];
                              handleManualAction(
                                primaryAction.action,
                                message.parameters,
                              );
                            }}
                            isDark={isDarkMode}
                          />
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    } else {
                      // Fallback to regular message bubble
                      return (
                        <div
                          className={`max-w-[80%] p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
                        >
                          <p className="text-xs leading-relaxed">
                            {message.content}
                          </p>
                          <p className="text-[10px] opacity-60 mt-0.5">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }
                  })()
                ) : message.actionStatus === "completed" &&
                  message.actionType === "create_project" &&
                  message.result &&
                  message.result.summary &&
                  message.result.summary.projectNumber ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectSummaryCard
                      message={message.content}
                      summary={message.result.summary}
                      projectId={
                        message.result.projectId?.toString() ||
                        message.result.projectId
                      }
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (
                  <div
                    className={`max-w-[80%] p-2 rounded-lg ${
                      message.role === "user"
                        ? "bg-primary-500 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    <p className="text-xs leading-relaxed">{message.content}</p>
                    <p className="text-[10px] opacity-60 mt-0.5">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Selected Files Display */}
        {featureEnabled && selectedFiles.length > 0 && (
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 rounded text-xs"
                >
                  <FileText className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {file.name}
                  </span>
                  <button
                    onClick={() =>
                      setSelectedFiles((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                    className="text-error-500 hover:text-error-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        {featureEnabled && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    setSelectedFiles((prev) => [
                      ...prev,
                      ...Array.from(e.target.files!),
                    ]);
                  }
                }}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.webp"
              />
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t("ai.placeholder")}
                className="flex-1 resize-none px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                aria-label="Chat input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                aria-label="Upload files"
                title="Upload files"
              >
                <Upload className="w-4 h-4" />
              </button>
              <button
                onClick={handleVoiceToggle}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  voiceConnected
                    ? "bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
                aria-label={t("ai.voiceChat")}
                title={t("ai.voiceChat")}
              >
                {voiceConnected ? (
                  <Mic className="w-4 h-4 animate-pulse" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() && selectedFiles.length === 0}
                className="px-3 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Pane */}
      <div
        className={`md:hidden fixed ${isExpanded ? "left-0 right-0 bottom-0" : "left-0 right-0 bottom-16"} bg-white dark:bg-gray-800 z-30 flex flex-col`}
        style={{ top: isExpanded ? topOffset : "56px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">
              {t("ai.title")}
            </h3>
            {isDevelopment && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 rounded">
                DEV
              </span>
            )}
          </div>
          <div className="flex items-center space-x-1 flex-shrink-0">
            {featureEnabled && (
              <>
                <button
                  onClick={startNewConversation}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={t("ai.newConversation")}
                  title={t("ai.newConversation")}
                >
                  <Plus className="w-5 h-5" />
                </button>
                <button
                  onClick={clearCurrentConversation}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={t("ai.clear")}
                  title={t("ai.clear")}
                >
                  <RotateCcw className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowConversations(!showConversations)}
                  className={`p-2 rounded-lg transition-colors ${
                    showConversations
                      ? "bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400"
                      : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }`}
                  aria-label={t("ai.conversations")}
                  title={t("ai.conversations")}
                >
                  <Clock className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label={isExpanded ? "Minimize" : "Expand"}
                  title={
                    isExpanded ? t("ai.minimize") : t("ai.expandToFullScreen")
                  }
                >
                  {isExpanded ? (
                    <Minimize2 className="w-5 h-5" />
                  ) : (
                    <Maximize2 className="w-5 h-5" />
                  )}
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close AI chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Voice Visualizer */}
        {featureEnabled &&
          voiceConnected &&
          (voiceRecording || voiceSpeaking) && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {voiceRecording
                    ? t("ai.voiceListening")
                    : t("ai.voiceSpeaking")}
                </span>
                <button
                  onClick={voiceDisconnect}
                  className="text-xs text-gray-500 hover:text-error-600 dark:text-gray-400 dark:hover:text-error-400"
                >
                  {t("common.disconnect")}
                </button>
              </div>
              <VoiceVisualizer
                isActive={true}
                type={voiceRecording ? "user" : "assistant"}
              />
            </div>
          )}

        {/* Demo Prompts (Dev Mode Only) */}
        {isDevelopment && featureEnabled && (
          <DemoPrompts onSelectPrompt={handleDemoPromptSelect} />
        )}

        {/* Messages or Coming Soon */}
        {!featureEnabled ? (
          <div className="flex-1 flex items-center justify-center">
            <ComingSoon
              featureName="Buddy AI Chat"
              description="AI-powered chat assistant for project management and business insights"
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {loading && (
              <div className="text-center text-xs text-gray-500 dark:text-gray-400">
                {t("ai.loadingConversation")}
              </div>
            )}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === "user" ? "justify-end" : message.role === "system" ? "justify-center" : "justify-start"}`}
              >
                {/* System "thinking" messages */}
                {message.role === "system" ? (
                  <div className="text-[10px] text-gray-500 dark:text-gray-400 italic py-1 px-2">
                    {message.content}
                  </div>
                ) : message.type === "recommendation" &&
                  message.recommendation &&
                  message.recommendation.suggestedAction ? (
                  <div className="max-w-[85%] w-full">
                    <RecommendationCard
                      message={message.content}
                      recommendation={message.recommendation}
                      parameters={message.parameters || {}}
                      onConfirm={() => handleRecommendationConfirm(message)}
                      onModify={(field) =>
                        handleRecommendationModify(message, field)
                      }
                      onManual={() =>
                        handleManualAction(
                          "open_" +
                            (message.actionType?.replace("create_", "") ||
                              "timesheet") +
                            "_modal",
                          message.parameters,
                        )
                      }
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.type === "plan_approval_request" &&
                  message.plan &&
                  message.actions ? (
                  <>
                    {console.log(
                      "[AIPane] 🎯 Rendering PlanApprovalCard for message:",
                      message.id,
                      {
                        type: message.type,
                        hasPlan: !!message.plan,
                        hasActions: !!message.actions,
                        planSteps: message.plan?.steps?.length,
                        actionsCount: message.actions?.length,
                      },
                    )}
                    <div className="max-w-[85%] w-full">
                      <PlanApprovalCard
                        message={message.content}
                        plan={message.plan}
                        actions={message.actions}
                        onAction={handlePlanAction}
                        isDark={isDarkMode}
                      />
                      <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </>
                ) : message.type === "entity_confirmation" ? (
                  <div className="max-w-[85%] w-full">
                    <EntityConfirmationCard
                      message={message.content}
                      parameters={message.parameters || {}}
                      fieldMetadata={message.fieldMetadata}
                      actionType={message.actionType || ""}
                      onConfirm={() => handleEntityConfirmation(message)}
                      onEdit={(field) => handleEntityEdit(message, field)}
                      onManual={() =>
                        handleManualAction(
                          message.availableActions?.[0]?.action ||
                            "open_project_modal",
                          message.parameters,
                        )
                      }
                      isDark={isDarkMode}
                      availableActions={message.availableActions}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (message.type === "clarification_needed" ||
                    message.type === "optional_fields_prompt") &&
                  message.clarificationOptions ? (
                  <div className="max-w-[85%] w-full">
                    <RecommendOptions
                      message={message.content}
                      clarificationType={message.clarificationType}
                      clarificationField={message.clarificationField}
                      clarificationFieldType={message.clarificationFieldType}
                      clarificationPlaceholder={
                        message.clarificationPlaceholder
                      }
                      multiSelect={message.multiSelect}
                      options={message.clarificationOptions || []}
                      onSelect={(option) =>
                        handleClarificationSelect(option, message.id)
                      }
                      isDark={isDarkMode}
                      availableActions={message.availableActions}
                      onManualAction={(action) =>
                        handleManualAction(action, message.parameters)
                      }
                      isCollapsed={message.clarificationAnswered}
                      display={message.display}
                      isOptionalPrompt={message.type === "optional_fields_prompt"}
                    />
                    {!message.clarificationAnswered && (
                      <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                        {message.timestamp.toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                ) : message.projectPreview ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectPreviewMessage
                      message={message.content}
                      projectPreview={message.projectPreview}
                      onConfirm={() => {
                        setMessages((prev) =>
                          prev.map((msg) =>
                            msg.id === message.id
                              ? {
                                  ...msg,
                                  content: `${msg.content}\n\n✅ ${t("project.createdSuccessfully")}`,
                                }
                              : msg,
                          ),
                        );
                      }}
                      onEdit={() => {
                        window.location.href = `/projects/${message.projectPreview?.projectId}`;
                      }}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (() => {
                    // Debug logging for get_project_summary rendering
                    if (message.actionType === "get_project_summary") {
                      console.log(
                        "[AIPane] 🐛 DEBUG get_project_summary rendering check:",
                        {
                          actionType: message.actionType,
                          hasResult: !!message.result,
                          resultType: typeof message.result,
                          resultKeys: message.result
                            ? Object.keys(message.result)
                            : [],
                          hasProjectNumber: message.result?.projectNumber,
                          projectNumber: message.result?.projectNumber,
                          conditionMatch:
                            message.actionType === "get_project_summary" &&
                            message.result &&
                            message.result.projectNumber,
                        },
                      );
                    }
                    return (
                      message.actionType === "get_project_summary" &&
                      message.result &&
                      message.result.projectNumber
                    );
                  })() ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectSummaryCard
                      message={message.content}
                      summary={message.result}
                      projectId={message.parameters?.project_id}
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.searchResults &&
                  Array.isArray(message.searchResults) &&
                  message.searchResults.length > 0 ? (
                  <div className="max-w-[85%] w-full">
                    <SearchResultCard
                      results={message.searchResults}
                      query={message.content}
                      onResultClick={(result) => {
                        if (result.url) {
                          window.location.href = result.url;
                        }
                      }}
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : message.role === "assistant" &&
                  (message.detectedEntities ||
                    (message.availableActions &&
                      message.availableActions.length > 0)) ? (
                  (() => {
                    // CRITICAL: Check for get_project_summary FIRST before any other conditions
                    if (
                      message.actionType === "get_project_summary" &&
                      message.result &&
                      message.result.projectNumber
                    ) {
                      console.log(
                        "[AIPane] 🐛 DEBUG: Rendering ProjectSummaryCard from detectedEntities block",
                      );
                      return (
                        <div className="max-w-[85%] w-full">
                          <ProjectSummaryCard
                            message={message.content}
                            summary={message.result}
                            projectId={message.parameters?.project_id}
                            isDark={isDarkMode}
                          />
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }

                    // CRITICAL: Don't show recommendation card for completed actions
                    // Only show for clarification/recommendation scenarios
                    if (message.actionStatus === "completed") {
                      console.log("[AIPane] Rendering completed action:", {
                        actionType: message.actionType,
                        actionStatus: message.actionStatus,
                        hasResult: !!message.result,
                        hasSummary: !!message.result?.summary,
                        summaryProjectNumber:
                          message.result?.summary?.projectNumber,
                        hasFieldMetadata: !!message.fieldMetadata,
                        fieldMetadataLength: message.fieldMetadata?.length || 0,
                      });

                      // PRIORITY 1: Check if this is a project creation with summary data - show ProjectSummaryCard
                      if (
                        message.actionType === "create_project" &&
                        message.result &&
                        message.result.summary &&
                        message.result.summary.projectNumber
                      ) {
                        console.log(
                          "[AIPane] ✅ Rendering ProjectSummaryCard for create_project",
                        );
                        return (
                          <div className="max-w-[85%] w-full">
                            <ProjectSummaryCard
                              message={message.content}
                              summary={message.result.summary}
                              projectId={
                                message.result.projectId?.toString() ||
                                message.result.projectId
                              }
                              isDark={isDarkMode}
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      }

                      // PRIORITY 2: Check if this is a project summary request
                      if (
                        message.actionType === "get_project_summary" &&
                        message.result &&
                        message.result.projectNumber
                      ) {
                        return (
                          <div className="max-w-[85%] w-full">
                            <ProjectSummaryCard
                              message={message.content}
                              summary={message.result}
                              projectId={message.parameters?.project_id}
                              isDark={isDarkMode}
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      }

                      // PRIORITY 3: Check if fieldMetadata is present - show EntityConfirmationCard instead of plain text
                      if (
                        message.fieldMetadata &&
                        Array.isArray(message.fieldMetadata) &&
                        message.fieldMetadata.length > 0 &&
                        message.parameters
                      ) {
                        console.log(
                          "[AIPane] ✅ Rendering EntityConfirmationCard with",
                          message.fieldMetadata.length,
                          "fields",
                        );
                        return (
                          <div className="max-w-[85%] w-full">
                            <EntityConfirmationCard
                              message={message.content}
                              parameters={message.parameters || {}}
                              fieldMetadata={message.fieldMetadata}
                              actionType={message.actionType || ""}
                              onConfirm={() => {}} // No action needed on success
                              onEdit={() => {}} // No edit needed on success
                              onManual={() => {}} // No manual action needed on success
                              isDark={isDarkMode}
                              availableActions={[]} // No actions needed on success
                              showActions={false} // Hide action buttons on success
                            />
                            <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                              {message.timestamp.toLocaleTimeString()}
                            </p>
                          </div>
                        );
                      } else {
                        console.log(
                          "[AIPane] ⚠️ Not showing EntityConfirmationCard - missing fieldMetadata or parameters",
                        );
                      }
                      // Show success message for other completed actions
                      return (
                        <div className="max-w-[85%] w-full">
                          <div
                            className={`rounded-lg p-3 ${
                              isDarkMode
                                ? "bg-success-900/20 border border-green-800/50"
                                : "bg-green-50 border border-green-200"
                            }`}
                          >
                            <p
                              className={`text-sm ${isDarkMode ? "text-success-300" : "text-success-800"}`}
                            >
                              ✅ {message.content}
                            </p>
                          </div>
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }

                    const recommendation = convertToRecommendation(message);
                    if (
                      recommendation &&
                      message.availableActions &&
                      message.availableActions.length > 0
                    ) {
                      return (
                        <div className="max-w-[85%] w-full">
                          <RecommendationCard
                            message={message.content}
                            recommendation={recommendation}
                            parameters={message.parameters || {}}
                            onConfirm={() => {
                              // When confirmed, trigger the primary manual action
                              const primaryAction =
                                message.availableActions![0];
                              handleManualAction(
                                primaryAction.action,
                                message.parameters,
                              );
                            }}
                            onModify={(field) => {
                              // Find the action for this field and trigger it
                              const action = message.availableActions?.find(
                                (a) => a.action.includes(field),
                              );
                              if (action) {
                                handleManualAction(
                                  action.action,
                                  message.parameters,
                                );
                              }
                            }}
                            onManual={() => {
                              // Trigger the primary manual action
                              const primaryAction =
                                message.availableActions![0];
                              handleManualAction(
                                primaryAction.action,
                                message.parameters,
                              );
                            }}
                            isDark={isDarkMode}
                          />
                          <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    } else {
                      // Fallback to regular message bubble
                      return (
                        <div
                          className={`max-w-[80%] p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100`}
                        >
                          <p className="text-xs leading-relaxed">
                            {message.content}
                          </p>
                          <p className="text-[10px] opacity-60 mt-0.5">
                            {message.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      );
                    }
                  })()
                ) : message.actionStatus === "completed" &&
                  message.actionType === "create_project" &&
                  message.result &&
                  message.result.summary &&
                  message.result.summary.projectNumber ? (
                  <div className="max-w-[85%] w-full">
                    <ProjectSummaryCard
                      message={message.content}
                      summary={message.result.summary}
                      projectId={
                        message.result.projectId?.toString() ||
                        message.result.projectId
                      }
                      isDark={isDarkMode}
                    />
                    <p className="text-[10px] opacity-60 mt-0.5 px-1 text-gray-400">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                ) : (
                  <div
                    className={`max-w-[80%] p-2 rounded-lg ${
                      message.role === "user"
                        ? "bg-primary-500 text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    }`}
                  >
                    <p className="text-xs leading-relaxed">{message.content}</p>
                    <p className="text-[10px] opacity-60 mt-0.5">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}

        {/* Selected Files Display - Mobile */}
        {featureEnabled && selectedFiles.length > 0 && (
          <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center gap-1 px-2 py-1 bg-white dark:bg-gray-700 rounded text-xs"
                >
                  <FileText className="w-3 h-3 text-gray-400" />
                  <span className="text-gray-700 dark:text-gray-300">
                    {file.name}
                  </span>
                  <button
                    onClick={() =>
                      setSelectedFiles((prev) =>
                        prev.filter((_, i) => i !== index),
                      )
                    }
                    className="text-error-500 hover:text-error-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        {featureEnabled && (
          <div className="p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="flex space-x-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={(e) => {
                  if (e.target.files) {
                    setSelectedFiles((prev) => [
                      ...prev,
                      ...Array.from(e.target.files!),
                    ]);
                  }
                }}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.webp"
              />
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={t("ai.placeholder")}
                className="flex-1 resize-none px-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                aria-label="Chat input"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                aria-label="Upload files"
                title="Upload files"
              >
                <Upload className="w-4 h-4" />
              </button>
              <button
                onClick={handleVoiceToggle}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  voiceConnected
                    ? "bg-success-100 dark:bg-success-900/30 text-success-600 dark:text-success-400"
                    : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                }`}
                aria-label={t("ai.voiceChat")}
                title={t("ai.voiceChat")}
              >
                {voiceConnected ? (
                  <Mic className="w-4 h-4 animate-pulse" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() && selectedFiles.length === 0}
                className="px-3 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
